import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo/AcrossLogo.png";
import titleLogo from "../assets/logo/AboutUsLogo.png";
import onScrolllogo from "../assets/logo/AcrossLogo.png";
import "./Style.css";

interface NavItem {
  label: string;
  route: string;
  subItems?: NavItem[];
}

interface DropdownMenu {
  label: string;
  items: NavItem[];
}

const gadgetANDaplliance: NavItem[] = [
  { label: "Home Appliances", route: "/home-appliances" },
  { label: "Mobile Protection", route: "/mobile-protection" },
];
const othersItems: NavItem[] = [
  { label: "Home Assistance", route: "/home-assistance" },
  { label: "Income Protect", route: "/income-protect" },
  { label: "Cyber Security", route: "/cyber-security" },
];

const productItems: NavItem[] = [
  { label: "Our Products", route: "/products" },
  { label: "Travel Assistance", route: "/travel-assistance" },
  { label: "Automotive Assistance", route: "/automotive-assistance" },
  {
    label: "Gadget & Appliance",
    route: "#",
    subItems: gadgetANDaplliance,
  },
  {
    label: "Others",
    route: "#",
    subItems: othersItems,
  },
];

const companyItems: NavItem[] = [
  { label: "About Us", route: "/about-us" },
  { label: "Careers", route: "/career" },
  { label: "Blogs", route: "/blogs" },
  { label: "Become a Partner", route: "/become-partner" },
];

const menus: DropdownMenu[] = [
  { label: "Products", items: productItems },
  { label: "Company", items: companyItems },
];

// Chevron Icon
const ChevronDown = ({ className }: { className?: string }) => (
  <svg
    className={className || "w-4 h-4"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const ChevronRight = ({ className }: { className?: string }) => (
  <svg
    className={className || "w-3 h-3 ml-1"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | undefined>(
    undefined
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState<
    string | null
  >(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState<string | null>(
    null
  );
  const [activeMobileSubDropdown, setActiveMobileSubDropdown] = useState<
    string | null
  >(null);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [submenuPosition, setSubmenuPosition] = useState<"left" | "right">("right");

  const subDropdownButtonRef = useRef<HTMLDivElement>(null);

  const getLogoSrc = () => {
    return isScrolled ? onScrolllogo : logo;
  };

  // Calculate if there is enough space on the right, otherwise submenu opens to the left
  const calculateSubmenuPosition = (event: React.MouseEvent, parentLabel: string, itemLabel: string) => {
    if (subDropdownButtonRef.current) {
      const rect = subDropdownButtonRef.current.getBoundingClientRect();
      const winWidth = window.innerWidth;
      // 240 is approximate width of submenu
      if (winWidth - rect.right < 240) {
        setSubmenuPosition("left");
      } else {
        setSubmenuPosition("right");
      }
    } else {
      setSubmenuPosition("right");
    }
  };

  // Returns submenu position class for desktop submenu (left/right of parent item)
  const getSubMenuPosition = () => {
    return submenuPosition === "right"
      ? "left-full top-0"
      : "right-full top-0";
  };

  const handleMouseEnter = (name: string) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(undefined);
    }
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
      setActiveSubDropdown(null);
    }, 150);
    setCloseTimeout(timeout);
  };

  const handleMenuItemClick = (route: string) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(undefined);
    }
    setActiveDropdown(null);
    setActiveSubDropdown(null);
    setIsMobileMenuOpen(false);
    setActiveMobileDropdown(null);
    setActiveMobileSubDropdown(null);
    if (route !== "#") {
      navigate(route);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setActiveMobileDropdown(null);
      setActiveMobileSubDropdown(null);
    }
  };

  const toggleMobileDropdown = (menuLabel: string) => {
    setActiveMobileDropdown(
      activeMobileDropdown === menuLabel ? null : menuLabel
    );
    setActiveMobileSubDropdown(null);
  };

  const toggleMobileSubDropdown = (itemLabel: string) => {
    setActiveMobileSubDropdown(
      activeMobileSubDropdown === itemLabel ? null : itemLabel
    );
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setActiveMobileDropdown(null);
        setActiveMobileSubDropdown(null);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Across Assist";
    // Set favicon to company logo (React-friendly)
    const setFavicon = (iconUrl: string) => {
      let link: HTMLLinkElement | null =
        document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        link.type = "image/png";
        document.head.appendChild(link);
      }
      link.href = iconUrl;
    };
    setFavicon(titleLogo);
  }, [pathname, titleLogo]);

  // Determine if current page is home/contact/blogs-details/PageNotFound for header color
  const isHomePage =
    location.pathname === "/" ||
    location.pathname === "/contact" ||
    location.pathname === "/blogs-details" ||
    location.pathname === "/*" ||
    location.pathname === "/page-not-found";

  return (
    <>
      {/* Navbar */}
      <div
        className={`fixed w-full transition-all duration-300 ease-in-out main-header-container
          ${
            isScrolled
              ? "bg-white bg-opacity-90 top-0 text-black scroll-header-main-container"
              : "bg-transparent text-white "
          }`}
      >
        <div
          className={`max-w-screen-xl mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between desktop-navbar-container`}
        >
          {/* Logo */}
          <div className="images-logo-container">
            <img
              src={getLogoSrc()}
              alt="Across Assist"
              className="h-8 sm:h-10 md:h-12 cursor-pointer"
              onClick={() => navigate("/")}
            />
          </div>

          {/* Desktop Nav Links */}
          <div
            className={`hidden desktop-navbar-items lg:flex items-center gap-4 xl:gap-8 font-medimm text-sm xl:text-base ${
              isScrolled ? "text-black" : "text-white"
            }`}
          >
            <button
              onClick={() => navigate("/")}
              className={`hover:text-blue-600 transition-colors ${
                isScrolled
                  ? "text-black"
                  : isHomePage
                  ? "font-color-header-home"
                  : "font-color-header-other"
              }`}
            >
              Home
            </button>

            {menus.map((menu) => (
              <div
                key={menu.label}
                className="relative"
                onMouseEnter={() => handleMouseEnter(menu.label)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`hover:text-blue-600 transition-colors flex items-center ${
                    isScrolled
                      ? "text-black"
                      : isHomePage
                      ? "font-color-header-home"
                      : "font-color-header-other"
                  }`}
                >
                  <span>{menu.label}</span>
                  <span className="ml-1">
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        activeDropdown === menu.label ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>
                {activeDropdown === menu.label && (
                  <div
                    className="absolute top-full left-0 mt-2 bg-white bg-opacity-90 rounded-md shadow-lg w-48 xl:w-52 z-50"
                    onMouseEnter={() => closeTimeout && clearTimeout(closeTimeout)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {menu.items.map((item) => (
                      <div
                        key={item.route}
                        className={`relative group`}
                        onMouseEnter={e => {
                          if (item.subItems) {
                            // Make sure to measure the DOM for submenu position
                            setActiveSubDropdown(item.label);
                            setTimeout(() => {
                              // we need this to ensure ref has been set in DOM
                              calculateSubmenuPosition(e, menu.label, item.label);
                            }, 1);
                          } else {
                            setActiveSubDropdown(null);
                          }
                        }}
                        onMouseLeave={() => {
                          if (item.subItems) setActiveSubDropdown(null);
                        }}
                        ref={
                          item.subItems && activeSubDropdown === item.label
                            ? subDropdownButtonRef
                            : undefined
                        }
                      >
                        <div
                          className={`px-3 xl:px-4 py-2 hover:bg-gray-100 cursor-pointer text-xs xl:text-sm transition-colors text-black flex items-center justify-between`}
                          onClick={() => handleMenuItemClick(item.route)}
                        >
                          <span className="flex items-center">
                            {item.label}
                            {item.subItems && (
                              <span>
                                <ChevronRight
                                  className="w-3 h-3 ml-2"
                                />
                              </span>
                            )}
                          </span>
                        </div>
                        {/* Submenu for items with subItems */}
                        {item.subItems && activeSubDropdown === item.label && (
                          <div
                            className={`absolute ${getSubMenuPosition()} mt-0 bg-white bg-opacity-95 rounded-md shadow-lg w-48 xl:w-52 z-50`}
                            style={{ minWidth: "180px" }}
                          >
                            {item.subItems.map((subItem) => (
                              <div
                                key={subItem.route}
                                className="px-3 xl:px-4 py-2 hover:bg-gray-100 cursor-pointer text-xs xl:text-sm transition-colors text-black flex items-center"
                                onClick={() => handleMenuItemClick(subItem.route)}
                              >
                                {subItem.label}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={() => navigate("/contact")}
              className={`px-3 xl:px-4 py-2 rounded-full transition relative overflow-hidden border-2 border-blue-500 text-xs xl:text-sm  ${
                location.pathname === "/" ? " home-page-container" : ""
              }
                ${
                  isScrolled
                    ? "text-black"
                    : isHomePage
                    ? "font-color-header-home"
                    : "font-color-header-other"
                }
              `}
              style={{
                backgroundSize: "200% 100%",
                backgroundPosition: "right center",
                transition:
                  "background-position 0.5s cubic-bezier(0.4,0,0.2,1), border-color 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundImage =
                  "linear-gradient(to left, #3b82f6, #60a5fa)";
                e.currentTarget.style.backgroundPosition = "left center";
                e.currentTarget.style.borderColor = "transparent";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundImage = "";
                e.currentTarget.style.backgroundPosition = "right center";
                e.currentTarget.style.borderColor = "#3b82f6";
              }}
            >
              Contact Us
            </button>
          </div>

          {/* Tablet Nav Links (Simplified) */}
          <div
            className={`hidden md:flex lg:hidden items-center gap-4 font-medium text-sm ${
              isScrolled ? "text-black" : "text-white"
            }`}
          >
            <button
              onClick={() => navigate("/")}
              className={`hover:text-blue-600 transition-colors ${
                isScrolled
                  ? "text-black"
                  : isHomePage
                  ? "font-color-header-home"
                  : "font-color-header-other"
              }`}
            >
              Home
            </button>
            <button
              onClick={() => navigate("/products")}
              className={`hover:text-blue-600 transition-colors ${
                isScrolled
                  ? "text-black"
                  : isHomePage
                  ? "font-color-header-home"
                  : "font-color-header-other"
              }`}
            >
              Products
            </button>
            <button
              onClick={() => navigate("/about-us")}
              className={`hover:text-blue-600 transition-colors ${
                isScrolled
                  ? "text-black"
                  : isHomePage
                  ? "font-color-header-home"
                  : "font-color-header-other"
              }`}
            >
              About
            </button>
            <button
              onClick={() => navigate("/contact")}
              className={`px-3 py-2 rounded-full transition relative overflow-hidden border-2 border-blue-500 text-sm
                ${
                  isScrolled
                    ? "text-black hover:text-white"
                    : "text-white hover:text-white"
                }
              `}
              style={{
                backgroundSize: "200% 100%",
                backgroundPosition: "right center",
                transition:
                  "background-position 0.5s cubic-bezier(0.4,0,0.2,1), border-color 0.3s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundImage =
                  "linear-gradient(to left, #3b82f6, #60a5fa)";
                e.currentTarget.style.backgroundPosition = "left center";
                e.currentTarget.style.borderColor = "transparent";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundImage = "";
                e.currentTarget.style.backgroundPosition = "right center";
                e.currentTarget.style.borderColor = "#3b82f6";
              }}
            >
              Contact
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 transition-colors ${
              isScrolled
                ? "text-black hover:text-blue-600"
                : isHomePage
                ? "font-color-header-home hover:text-blue-600"
                : "font-color-header-other hover:text-blue-300"
            }`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white bg-opacity-90 border-t border-gray-200 shadow-lg">
            <div className="px-4 py-2 space-y-2">
              <button
                onClick={() => handleMenuItemClick("/")}
                className="w-full text-left py-3 px-4 text-black hover:bg-gray-100 rounded-md transition-colors font-medium"
              >
                Home
              </button>

              {menus.map((menu) => (
                <div key={menu.label}>
                  <button
                    onClick={() => toggleMobileDropdown(menu.label)}
                    className="w-full text-left py-3 px-4 text-black hover:bg-gray-100 rounded-md transition-colors font-medium flex justify-between items-center"
                  >
                    <span>{menu.label}</span>
                    <span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          activeMobileDropdown === menu.label ? "rotate-180" : ""
                        }`}
                      />
                    </span>
                  </button>
                  {activeMobileDropdown === menu.label && (
                    <div className="ml-4 space-y-1">
                      {menu.items.map((item) => (
                        <div key={item.route}>
                          <button
                            onClick={() => {
                              if (item.subItems) {
                                toggleMobileSubDropdown(item.label);
                              } else {
                                handleMenuItemClick(item.route);
                              }
                            }}
                            className="w-full text-left py-2 px-4 text-gray-600 hover:bg-gray-50 rounded-md transition-colors text-sm flex justify-between items-center"
                          >
                            <span className="flex items-center">
                              {item.label}
                              {item.subItems && (
                                <span>
                                  <ChevronRight
                                    className={`w-3 h-3 ml-2 transition-transform ${
                                      activeMobileSubDropdown === item.label
                                        ? "rotate-90"
                                        : ""
                                    }`}
                                  />
                                </span>
                              )}
                            </span>
                          </button>
                          {/* Submenu for mobile */}
                          {item.subItems &&
                            activeMobileSubDropdown === item.label && (
                              <div className="ml-4 space-y-1">
                                {item.subItems.map((subItem) => (
                                  <button
                                    key={subItem.route}
                                    onClick={() =>
                                      handleMenuItemClick(subItem.route)
                                    }
                                    className="w-full text-left py-2 px-4 text-gray-600 hover:bg-gray-100 rounded-md transition-colors text-sm"
                                  >
                                    {subItem.label}
                                  </button>
                                ))}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <button
                onClick={() => handleMenuItemClick("/contact")}
                className="w-full text-center py-3 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium mt-4"
              >
                Contact Us
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Spacer for fixed header */}
      <div className="h-[60px] sm:h-[60px] md:h-[60px] lg:h-[60px]" />
    </>
  );
};

export default Header;
